// Top of the file
const originBase = (typeof window !== 'undefined' ? window.location.origin : '');
const DEFAULT_API = originBase ? `${originBase}/api/v1` : '/api/v1';

// Base configuration
const API_CONFIG = {
  API: {
    // Prefer envs; then same-origin '/api/v1' (works behind Nginx)
    baseUrl:
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      DEFAULT_API,

    timeout: 15000,
    withCredentials: true,
    retryAttempts: 3,
    retryDelay: 1000,
  },

  // Security Configuration
  SECURITY: {
    enforceHttps: true,
    csrfProtection: true,
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    idleTimeout: 15 * 60 * 1000, // 15 minutes
    maxLoginAttempts: 5,
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
  },

  // Error Reporting
  ERROR_REPORTING: {
    enabled: true,
    logLevel: 'error',
    maxErrors: 10,
    reportToService: true,
  },

  // Performance Monitoring
  PERFORMANCE: {
    trackPageLoads: true,
    trackUserInteractions: true,
    sampleRate: 0.1, // 10% of sessions
  },

  // Feature Flags
  FEATURES: {
    debugMode: false,
    maintenanceMode: process.env.REACT_APP_MAINTENANCE_MODE === 'true',
    newUserRegistration: process.env.REACT_APP_ALLOW_REGISTRATION !== 'false',
    adminPanel: process.env.REACT_APP_ADMIN_ENABLED === 'true',
  },

  // Cache Configuration
  CACHE: {
    userProfileTTL: 5 * 60 * 1000, // 5 minutes
    staticDataTTL: 30 * 60 * 1000, // 30 minutes
    apiResponseTTL: 60 * 1000, // 1 minute
  },

  // Validation Rules
  VALIDATION: {
    password: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
    },
    username: {
      minLength: 3,
      maxLength: 30,
      allowedChars: /^[a-zA-Z0-9_-]+$/,
    },
    email: {
      maxLength: 254,
      regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    },
  },
};

// Environment-specific overrides
const ENVIRONMENT_OVERRIDES = {
  development: {
    API: {
      // No hardcoded baseUrl here -- it used to unconditionally overwrite
      // the env-driven value computed in API_CONFIG above (REACT_APP_BACKEND_URL
      // || REACT_APP_API_BASE_URL || same-origin fallback), via mergeConfigs.
      // Since CRA's `npm start` always sets NODE_ENV=development, that made
      // this override win on every single dev run regardless of what
      // REACT_APP_BACKEND_URL was set to -- silently pointing every API call
      // at a hardcoded port 8000 no matter the real backend's port.
      timeout: 30000,
    },
    SECURITY: {
      enforceHttps: false,
      sessionTimeout: 60 * 60 * 1000, // 1 hour
    },
    ERROR_REPORTING: {
      logLevel: 'debug',
      reportToService: false,
    },
    FEATURES: {
      debugMode: true,
    },
  },
  test: {
    API: {
      // Same reasoning as the development override above -- don't hardcode
      // a baseUrl here, let the env-driven value from API_CONFIG stand.
      timeout: 5000,
    },
    SECURITY: {
      enforceHttps: false,
      maxLoginAttempts: 10,
    },
    ERROR_REPORTING: {
      enabled: false,
    },
  },
  staging: {
    API: {
      baseUrl: 'https://api-staging.yourdomain.com/api/v1',
    },
    SECURITY: {
      enforceHttps: true,
    },
    ERROR_REPORTING: {
      logLevel: 'warn',
      sampleRate: 0.5,
    },
  },
};

// Get current environment
const getCurrentEnvironment = () => {
  if (process.env.NODE_ENV === 'development') return 'development';
  if (process.env.NODE_ENV === 'test') return 'test';
  if (process.env.REACT_APP_ENV === 'staging') return 'staging';
  return 'production';
};

// Deep merge utility
const mergeConfigs = (base, override) => {
  const result = { ...base };
  Object.keys(override).forEach(key => {
    if (typeof override[key] === 'object' && override[key] !== null && !Array.isArray(override[key])) {
      result[key] = mergeConfigs(base[key] || {}, override[key]);
    } else {
      result[key] = override[key];
    }
  });
  return result;
};

// Build final config
const environment = getCurrentEnvironment();
const environmentOverride = ENVIRONMENT_OVERRIDES[environment] || {};
const finalConfig = mergeConfigs(API_CONFIG, environmentOverride);
// Normalize once: no trailing slash, always something sane
finalConfig.API.baseUrl = (finalConfig.API.baseUrl || '/api/v1').replace(/\/$/, '');

// Runtime properties
finalConfig.RUNTIME = {
  environment,
  buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString(),
  version: process.env.REACT_APP_VERSION || '1.0.0',
  commitHash: process.env.REACT_APP_COMMIT_HASH || 'unknown',
};

// Validation
export const validateConfig = () => {
  const required = ['API.baseUrl'];
  const missing = [];
  required.forEach(path => {
    const keys = path.split('.');
    let current = finalConfig;
    for (const key of keys) {
      if (!current[key]) {
        missing.push(path);
        break;
      }
      current = current[key];
    }
  });
  if (missing.length > 0) throw new Error(`Missing required configuration: ${missing.join(', ')}`);
  return true;
};

// Helpers
export const isProduction = () => environment === 'production';
export const isDevelopment = () => environment === 'development';
export const isStaging = () => environment === 'staging';
export const isTest = () => environment === 'test';

// Debug log
if (isDevelopment()) {
  //console.log('[Config] Current environment:', environment);
  //console.log('[Config] Configuration loaded:', finalConfig);
}

// Export
export default finalConfig;
